import type { MutableRefObject } from 'react';
import clamp from 'lodash/clamp';
import compact from 'lodash/compact';
import { START_NODE_ID } from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import {
	START_NODE_RADIUS,
	STATUS_HEIGHT,
	MINIMUM_ZOOM_LEVEL,
	DEFAULT_ZOOM_LEVEL,
} from './ellipsis.tsx';
import {
	type DiagramStatus,
	PortIndex,
	type Point,
	toPoint,
	type DiagramDirectTransition,
	type DiagramSharedTransition,
	type DiagramSharedTransitionWithDefinedPorts,
	type DiagramDirectTransitionWithDefinedPorts,
} from './toPortInfo.tsx';
import type { Positionable } from './ListEdgeDetails.tsx';
import { angleBetween } from './percentageOfLine.tsx';
import { getBBox } from './isEveryPropertyDefined.tsx';
import { getCanvasBoundingBox, getBBoxCenter } from './getCanvasBoundingBox.tsx';
import type { ContentBounds } from './clampPan.tsx';

const STATUSES_PADDING_RIGHT = 60;
export const START_NODE_VERTICAL_OFFSET = 50;
export const START_NODE_PADDING_RIGHT = 80;

const DEFAULT_LOOP_SOURCE_INDEX = PortIndex.Bottom;
const DEFAULT_LOOP_TARGET_INDEX = PortIndex.Left;

export const bestGuessPort = (
	statuses: DiagramStatus[],
	sourceStatusId: string,
	targetStatusId: string,
	sourceOrTarget: 'source' | 'target',
): PortIndex => {
	const sourceStatus = statuses.find(({ id }) => id === sourceStatusId);
	const targetStatus = statuses.find(({ id }) => id === targetStatusId);

	if (sourceStatus?.position === undefined || targetStatus?.position === undefined) return 0;

	const angle =
		sourceOrTarget === 'source'
			? angleBetween(sourceStatus.position, targetStatus.position)
			: angleBetween(targetStatus.position, sourceStatus.position);

	if (angle >= 22.5 && angle < 67.5) {
		return PortIndex.BottomRight;
	}

	if (angle >= 67.5 && angle < 112.5) {
		return PortIndex.Bottom;
	}

	if (angle >= 112.5 && angle < 157.5) {
		return PortIndex.BottomLeft;
	}

	if (angle >= 157.5 && angle < 202.5) {
		return PortIndex.Left;
	}

	if (angle >= 202.5 && angle < 247.5) {
		return PortIndex.TopLeft;
	}

	if (angle >= 247.5 && angle < 292.5) {
		return PortIndex.Top;
	}

	if (angle >= 292.5 && angle < 337.5) {
		return PortIndex.TopRight;
	}

	// between 337.5º to 22.5º
	return PortIndex.Right;
};

export const getPositionableWidth = (
	positionable: Positionable,
	statusWidths: Record<string, number>,
): number =>
	positionable.type === 'start-node' ? START_NODE_RADIUS * 2 : statusWidths[positionable.id] || 0;

export const findDefaultPositions = (
	statusWidths: Record<string, number>,
	startNodePosition: Point | undefined,
	statuses: DiagramStatus[],
	defaultPositions: Record<string, Point>,
): Positionable[] => {
	const positioned: Positionable[] = [];

	if (!Object.keys(statusWidths).length) return positioned;

	const positionables = [
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		{
			type: 'start-node',
			id: START_NODE_ID,
			position: startNodePosition || defaultPositions[START_NODE_ID],
		} as Positionable,
		...statuses
			.sort((statusA, statusB) => 1 - Number(statusA.isInitial) - (1 - Number(statusB.isInitial)))
			.map(
				({ id, position }) =>
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					({
						type: 'status',
						id,
						position: position || defaultPositions[id],
					}) as Positionable,
			),
	];

	const positionablesWithUndefinedPositions = positionables.filter(
		({ position }) => position === undefined,
	);

	if (positionablesWithUndefinedPositions.length > 0) {
		let maxY: number | undefined;
		let minY: number | undefined;
		let maxX: number | undefined;

		positionables.forEach((positionable) => {
			if (!positionable.position) return;

			const statusWidth = getPositionableWidth(positionable, statusWidths);

			if (maxX === undefined || positionable.position.x + statusWidth > maxX) {
				maxX = positionable.position.x + statusWidth;
			}

			if (maxY === undefined || positionable.position.y + STATUS_HEIGHT > maxY) {
				maxY = positionable.position.y + STATUS_HEIGHT;
			}

			if (minY === undefined || positionable.position.y < minY) {
				minY = positionable.position.y;
			}
		});

		let x = (maxX || 0) + STATUSES_PADDING_RIGHT;
		const centerY = ((maxY || 0) + (minY || 0)) / 2;

		positionablesWithUndefinedPositions.forEach((positionable) => {
			const y = centerY - getVerticalOffset(positionable);
			positioned.push({ ...positionable, position: { x, y } });
			x = x + getPositionableWidth(positionable, statusWidths) + getRightPadding(positionable);
		});
	}

	return positioned;
};

export const getRightPadding = (positionable: Positionable): 80 | 60 =>
	positionable.type === 'start-node' ? START_NODE_PADDING_RIGHT : STATUSES_PADDING_RIGHT;

export const getVerticalOffset = (positionable: Positionable): number =>
	positionable.type === 'start-node'
		? START_NODE_VERTICAL_OFFSET + START_NODE_RADIUS
		: STATUS_HEIGHT / 2;

/**
 * Pure function to calculate the zoom level that fits content within a container.
 * Used by both autoFitCanvas and useAutoFitZoom for consistent behavior.
 */
export const calculateFitZoom = (
	contentBounds: { width: number; height: number },
	containerSize: { width: number; height: number },
	padding = 20,
): number => {
	const paddedWidth = contentBounds.width + padding * 2;
	const paddedHeight = contentBounds.height + padding * 2;

	if (paddedWidth <= 0 || paddedHeight <= 0) {
		return DEFAULT_ZOOM_LEVEL;
	}

	return Math.min(containerSize.height / paddedHeight, containerSize.width / paddedWidth);
};

export const autoFitCanvas = (
	rootSvgRef: MutableRefObject<HTMLElement | null>,
	canvasContainerRef: MutableRefObject<HTMLElement | null>,
	onPan: (point: Point) => void,
	onZoom: (zoom: number) => void,
	contentBounds?: ContentBounds,
): void => {
	if (!canvasContainerRef.current) return;

	const containerRect = canvasContainerRef.current.getBoundingClientRect();

	// Use state-based content bounds if provided, otherwise fall back to DOM measurement
	let boundingBox: ContentBounds | undefined = contentBounds;

	if (!boundingBox && rootSvgRef.current) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const children = Array.from(rootSvgRef.current.children) as SVGGraphicsElement[];
		const childBounds = compact(
			children
				.filter(
					(child) =>
						// Filter out elements with ignore auto fit boolean attribute
						// Note that this checks existence of attribute not value as
						// per definition of boolean attributes https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes#boolean_attributes
						!child.hasAttribute(DATA_ATTRIBUTE_IGNORE_AUTOFIT_KEY),
				)
				.map(getBBox),
		);

		boundingBox = getCanvasBoundingBox(childBounds);
	}

	if (!boundingBox) return;

	if (containerRect.width <= 0 || containerRect.height <= 0) return;

	const boundingBoxCenter = getBBoxCenter(boundingBox);
	onPan(toPoint(boundingBoxCenter.x, boundingBoxCenter.y));

	const zoomLevel = calculateFitZoom(boundingBox, containerRect);

	// When contentBounds is provided (experiment), allow any zoom level
	onZoom(
		contentBounds
			? Math.min(zoomLevel, DEFAULT_ZOOM_LEVEL)
			: clamp(zoomLevel, MINIMUM_ZOOM_LEVEL, DEFAULT_ZOOM_LEVEL),
	);
};

export const DATA_ATTRIBUTE_IGNORE_AUTOFIT_KEY = 'data-ignore-autofit' as const;
/** Lets autoFit calculations know to ignore element. Must be added to direct DOM children under rootSvg */
export const DATA_ATTRIBUTE_IGNORE_AUTOFIT: {
	readonly 'data-ignore-autofit': true;
} = { [DATA_ATTRIBUTE_IGNORE_AUTOFIT_KEY]: true } as const;

/**
 * Mockable wrapper for `setTimeout(callback, 0)`
 * @deprecated usages of setTimeout(callback, 0) will need to be removed to support concurrent React. No new usages should be added.
 */
export const deferredExecute = (callback: Function): number => setTimeout(callback, 0);

export const definePortsOnSharedTransition = (
	transition: DiagramSharedTransition,
	statuses: DiagramStatus[],
): DiagramSharedTransitionWithDefinedPorts => ({
	...transition,
	links: transition.links.map((link) => {
		if (link.source === transition.target) {
			return {
				...link,
				sourceIndex: DEFAULT_LOOP_SOURCE_INDEX,
				targetIndex: DEFAULT_LOOP_TARGET_INDEX,
			};
		}

		return {
			...link,
			sourceIndex:
				link.sourceIndex ?? bestGuessPort(statuses, link.source, transition.target, 'source'),
			targetIndex:
				link.targetIndex ?? bestGuessPort(statuses, link.source, transition.target, 'target'),
		};
	}),
});

export const definePortsOnDirectTransition = (
	transition: DiagramDirectTransition,
	statuses: DiagramStatus[],
): DiagramDirectTransitionWithDefinedPorts => {
	if (transition.source === transition.target) {
		return {
			...transition,
			sourceIndex: DEFAULT_LOOP_SOURCE_INDEX,
			targetIndex: DEFAULT_LOOP_TARGET_INDEX,
		};
	}

	return {
		...transition,
		sourceIndex:
			transition.sourceIndex ??
			bestGuessPort(statuses, transition.source, transition.target, 'source'),
		targetIndex:
			transition.targetIndex ??
			bestGuessPort(statuses, transition.source, transition.target, 'target'),
	};
};
